<template>
  <Teleport to="body">
    <Transition name="txn-audit-overlay">
      <div
        v-if="show"
        class="txn-audit-overlay txn-audit-overlay--glass"
        role="presentation"
        @click.self="handleClose"
      >
        <Transition name="txn-audit-panel" appear>
          <div
            v-if="show"
            class="txn-audit-modal txn-audit-modal--glass"
            role="dialog"
            aria-modal="true"
            aria-labelledby="txn-audit-title"
            title="Drag the bottom-right corner to resize"
            @click.stop
          >
        <header class="txn-audit-header">
          <div class="txn-audit-header-main">
            <div class="txn-audit-header-top">
              <span v-if="headerType" class="txn-audit-type-pill">{{ headerType }}</span>
              <h2 id="txn-audit-title" class="txn-audit-title">{{ headerTitle }}</h2>
            </div>
            <p v-if="headerMeta" class="txn-audit-header-meta">{{ headerMeta }}</p>
            <div v-if="headerChips.length" class="txn-audit-header-chips">
              <span
                v-for="(chip, i) in headerChips"
                :key="'hc-' + i"
                class="txn-audit-chip"
              >{{ chip }}</span>
            </div>
          </div>
          <button type="button" class="txn-audit-close" aria-label="Close" @click="handleClose">
            <i class="bi bi-x-lg" aria-hidden="true"></i>
          </button>
        </header>

        <div class="txn-audit-body">
          <div v-if="loading" class="txn-audit-state">
            <span class="txn-audit-spinner" aria-hidden="true"></span>
            <span>Loading activity…</span>
          </div>
          <div v-else-if="error" class="txn-audit-state txn-audit-state--error">
            <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
            <span>{{ error }}</span>
          </div>
          <div v-else-if="!events.length" class="txn-audit-state">
            <i class="bi bi-journal-text" aria-hidden="true"></i>
            <span>No activity recorded for this transaction.</span>
          </div>
          <ol v-else class="txn-audit-feed">
            <li
              v-for="event in events"
              :key="event.id"
              class="txn-audit-event"
              :class="`txn-audit-event--${event.type}`"
            >
              <span class="txn-audit-event-dot" aria-hidden="true">
                <i :class="event.icon"></i>
              </span>
              <div class="txn-audit-event-card">
                <p class="txn-audit-event-title">{{ event.title }}</p>
                <p class="txn-audit-event-meta">
                  <span v-if="event.actor && event.actor !== '—'">{{ event.actor }}</span>
                  <span v-if="event.actor && event.actor !== '—' && event.timeLabel"> · </span>
                  <time v-if="event.timeLabel" :datetime="event.timestampIso">{{ event.timeLabel }}</time>
                </p>
                <div v-if="event.chips.length" class="txn-audit-event-chips">
                  <span
                    v-for="(chip, ci) in event.chips"
                    :key="event.id + '-c-' + ci"
                    class="txn-audit-chip txn-audit-chip--sm"
                  >{{ chip }}</span>
                </div>
                <ul v-if="event.links.length" class="txn-audit-links">
                  <li
                    v-for="link in event.links"
                    :key="link.id"
                    class="txn-audit-link"
                  >
                    <span class="txn-audit-link-title">{{ link.title }}</span>
                    <span v-if="link.meta" class="txn-audit-link-meta">{{ link.meta }}</span>
                    <span v-if="link.note" class="txn-audit-link-note">{{ link.note }}</span>
                  </li>
                </ul>
              </div>
            </li>
          </ol>
        </div>

        <footer class="txn-audit-footer">
          <span v-if="events.length && !loading" class="txn-audit-footer-count">
            {{ events.length }} {{ events.length === 1 ? 'event' : 'events' }}
          </span>
          <PrimaryButton label="Done" icon="bi bi-check-lg" @click="handleClose" />
        </footer>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import logger from '@/utils/logger';
import ApiService from '@/services/api.service.js';
import '../styles/TransactionAuditLog.css';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';

dayjs.extend(relativeTime);

let eventSeq = 0;

function nextEventId(prefix) {
  eventSeq += 1;
  return `${prefix}-${eventSeq}`;
}

/** True when the source is a calendar date without a real time-of-day. */
function isDateOnlyValue(value) {
  if (value == null || value === '') return true;
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return true;
  if (/^\d{4}-\d{2}-\d{2}[ T]00:00:00/.test(raw)) return true;
  if (!/[T\s]\d{1,2}:\d{2}/.test(raw) && !raw.includes('AM') && !raw.includes('PM')) {
    const d = dayjs(value);
    if (d.isValid() && d.hour() === 0 && d.minute() === 0 && d.second() === 0) {
      return true;
    }
  }
  return false;
}

function formatDateShort(value) {
  if (!value) return '';
  const d = dayjs(value);
  return d.isValid() ? d.format('MMM D, YYYY') : '';
}

/** Prefer real audit timestamps; avoid misleading relative labels on date-only values. */
function formatAuditTime(value) {
  if (value == null || value === '') return { label: '', iso: '' };
  const d = dayjs(value);
  if (!d.isValid()) return { label: '', iso: '' };

  const iso = d.toISOString();

  if (isDateOnlyValue(value)) {
    return { label: d.format('MMM D, YYYY'), iso };
  }

  const absolute = d.format('MMM D, YYYY · h:mm A');
  const minutesAgo = Math.abs(dayjs().diff(d, 'minute'));
  if (minutesAgo < 60 * 48) {
    return { label: `${d.fromNow()} (${absolute})`, iso };
  }
  return { label: absolute, iso };
}

function pickInvoiceCreatedAt(details, invoice, transaction) {
  const logs = Array.isArray(details?.creationLogs) ? details.creationLogs : [];
  const fromLog = logs.find((l) => l?.timestamp)?.timestamp;
  return (
    fromLog ||
    invoice?.createdAt ||
    transaction?.createdAt ||
    null
  );
}

function clientDisplayName(client) {
  if (!client) return '';
  const name = [client.firstName, client.lastName].filter(Boolean).join(' ').trim();
  return name || client.email || '';
}

export default {
  name: 'TransactionAuditLog',
  components: {
    PrimaryButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    transaction: {
      type: Object,
      default: null
    },
    companyId: {
      type: [String, Number],
      default: null
    },
    timesheets: {
      type: Array,
      default: () => []
    },
    consultantName: {
      type: String,
      default: ''
    }
  },
  emits: ['close'],
  data() {
    return {
      loading: false,
      error: null,
      events: []
    };
  },
  computed: {
    resolvedCompanyId() {
      return this.companyId || this.$route?.params?.id || this.$route?.params?.companyId || null;
    },
    headerType() {
      return this.transaction?.type || '';
    },
    headerTitle() {
      const t = this.transaction;
      if (!t) return 'Activity';
      const ref =
        t.invoiceNo ||
        t.billNo ||
        t.receivePaymentNo ||
        t.payBillNo ||
        (t.customerInvoiceId != null ? `#${t.customerInvoiceId}` : '') ||
        '';
      return ref ? `${t.type || 'Transaction'} ${ref}` : t.type || 'Transaction';
    },
    headerMeta() {
      const t = this.transaction;
      if (!t) return '';
      return t.customer || t.vendorName || '';
    },
    headerChips() {
      const t = this.transaction;
      if (!t) return [];
      const chips = [];
      const amount = this.formatMoney(t.amount ?? t.totalAmount);
      if (amount !== '—') chips.push(amount);
      const status = t.status;
      if (status) chips.push(status);
      const date = formatDateShort(t.invoiceDate || t.billDate || t.date);
      if (date) chips.push(date);
      return chips;
    }
  },
  watch: {
    show(visible) {
      if (visible && this.transaction) {
        this.loadAuditTrail();
      } else if (!visible) {
        this.resetState();
      }
    },
    transaction: {
      handler() {
        if (this.show && this.transaction) {
          this.loadAuditTrail();
        }
      },
      deep: true
    }
  },
  methods: {
    handleClose() {
      this.$emit('close');
    },
    resetState() {
      this.loading = false;
      this.error = null;
      this.events = [];
      eventSeq = 0;
    },
    getCurrentUserDisplayName() {
      const user =
        this.$store?.getters?.['auth/currentUser'] ||
        (() => {
          try {
            return JSON.parse(localStorage.getItem('user') || 'null');
          } catch {
            return null;
          }
        })();
      if (!user) return '';
      const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
      if (full) return full;
      if (user.email) return String(user.email);
      if (user.username) return String(user.username);
      return '';
    },
    resolveActor(name) {
      const trimmed = name && String(name).trim();
      if (trimmed && trimmed.toLowerCase() !== 'system') {
        return trimmed;
      }
      return this.getCurrentUserDisplayName() || '';
    },
    sortEvents(list) {
      return [...list].sort((a, b) => (b.sortTime || 0) - (a.sortTime || 0));
    },
    dedupeEvents(list) {
      const seen = new Set();
      const result = [];
      for (const evt of list) {
        const bucket = Math.floor((evt.sortTime || 0) / 60000);
        const key = `${evt.type}|${bucket}|${evt.title}`;
        if (seen.has(key)) continue;
        seen.add(key);
        result.push(evt);
      }
      return result;
    },
    pushEvent(list, partial) {
      const ts = partial.timestamp ? new Date(partial.timestamp) : null;
      const sortTime = ts && !Number.isNaN(ts.getTime()) ? ts.getTime() : 0;
      const { label: timeLabel, iso: timestampIso } = formatAuditTime(partial.timestamp);
      const chips = (partial.chips || []).filter(Boolean);
      const links = partial.links || [];
      if (!partial.title && !chips.length && !links.length) return;

      list.push({
        id: nextEventId(partial.type || 'evt'),
        sortTime,
        timestampIso,
        timeLabel,
        actor: this.resolveActor(partial.actor),
        title: partial.title || '',
        type: partial.type || 'activity',
        icon: partial.icon || 'bi bi-circle-fill',
        chips,
        links
      });
    },
    findLocalTimesheet(consultantTimesheetId) {
      if (consultantTimesheetId == null || consultantTimesheetId === '') return null;
      const id = String(consultantTimesheetId);
      return (this.timesheets || []).find((ts) => String(ts.id) === id) || null;
    },
    buildTimesheetMeta(ts, consultantName, local) {
      const from = ts?.fromDate || local?.fromDate;
      const to = ts?.toDate || local?.toDate;
      const period =
        from && to
          ? `${formatDateShort(from)} – ${formatDateShort(to)}`
          : formatDateShort(from || to) || '';
      const hours = ts?.hours ?? local?.hours;
      const parts = [];
      if (period) parts.push(period);
      if (hours != null && hours !== '') parts.push(`${hours}h`);
      const status = ts?.status || local?.status;
      if (status) parts.push(status);
      return {
        id: String(ts?.id || local?.id || nextEventId('ts')),
        title: consultantName || this.consultantName || 'Consultant',
        meta: parts.join(' · ') || '—',
        hrTimesheetId: ts?.hrTimesheetId || local?.hrTimesheetId || null
      };
    },
    async fetchLatestHrNote(hrTimesheetId) {
      if (!hrTimesheetId || !this.resolvedCompanyId) return '';
      try {
        const response = await ApiService.get(`/timesheets/${hrTimesheetId}/history`, {
          params: { companyId: this.resolvedCompanyId }
        });
        const rows = response.data?.data || [];
        if (!rows.length) return '';
        const latest = rows[rows.length - 1];
        const action = latest.action || latest.newStatus || 'Updated';
        const who = this.resolveActor(clientDisplayName(latest.performedByClient));
        const when = formatDateShort(latest.performedAt);
        const bits = [action];
        if (who) bits.push(`by ${who}`);
        if (when) bits.push(when);
        return bits.join(' · ');
      } catch (err) {
        logger.warn('TransactionAuditLog: HR history fetch failed', err);
        return '';
      }
    },
    async loadAuditTrail() {
      const t = this.transaction;
      if (!t) return;

      this.loading = true;
      this.error = null;
      this.events = [];
      eventSeq = 0;

      try {
        const type = t.type || '';
        let built = [];

        if (type === 'Invoice' || type === 'Invoice Item') {
          built = await this.buildInvoiceAudit(t);
        } else if (type === 'Bill to be Paid') {
          built = await this.buildBillAudit(t);
        } else if (type === 'Received Payment') {
          built = this.buildReceivedPaymentAudit(t);
        } else if (type === 'Pay Bill') {
          built = this.buildPayBillAudit(t);
        } else {
          built = this.buildGenericAudit(t);
        }

        this.events = this.dedupeEvents(this.sortEvents(built));
      } catch (err) {
        logger.error('TransactionAuditLog: load failed', err);
        this.error = err.message || 'Failed to load activity';
        this.events = [];
      } finally {
        this.loading = false;
      }
    },
    async buildInvoiceAudit(transaction) {
      const events = [];
      const invoiceId = transaction.customerInvoiceId;
      const companyId = this.resolvedCompanyId;

      if (!invoiceId || !companyId) {
        this.pushEvent(events, {
          timestamp: transaction.date || transaction.invoiceDate,
          title: `Invoice ${transaction.invoiceNo || ''} recorded`.trim(),
          type: 'invoice',
          icon: 'bi bi-receipt',
          chips: [
            transaction.customer,
            this.formatMoney(transaction.amount),
            transaction.status
          ].filter(Boolean)
        });
        return events;
      }

      const [details, itemsRaw] = await Promise.all([
        ApiService.getInvoiceDetails(companyId, invoiceId).catch(() => null),
        ApiService.getInvoiceItemsByInvoice(invoiceId).catch(() => null)
      ]);

      const items =
        Array.isArray(itemsRaw) && itemsRaw.length
          ? itemsRaw
          : Array.isArray(transaction.invoiceLineItems)
            ? transaction.invoiceLineItems
            : [];

      const invoice = details?.invoice || {};
      const invoiceNo = invoice.invoiceNo || transaction.invoiceNo || `#${invoiceId}`;
      const customerName = invoice.customerName || transaction.customer || '';
      const invoiceCreatedAt = pickInvoiceCreatedAt(details, invoice, transaction);

      this.pushEvent(events, {
        timestamp: invoiceCreatedAt || invoice.date || transaction.invoiceDate || transaction.date,
        title: `Invoice ${invoiceNo} created`,
        type: 'invoice',
        icon: 'bi bi-receipt',
        chips: [
          customerName,
          formatDateShort(invoice.date || transaction.invoiceDate),
          this.formatMoney(invoice.totalAmount ?? transaction.amount),
          invoice.status || transaction.status
        ].filter(Boolean)
      });

      const itemList = Array.isArray(items) ? items : [];
      const timesheetLinks = [];
      const seenTs = new Set();
      const hrNoteCache = new Map();

      for (const item of itemList) {
        const ts = item.consultantTimesheet;
        if (!ts?.id || seenTs.has(ts.id)) continue;
        seenTs.add(ts.id);

        const local = this.findLocalTimesheet(ts.id);
        const name = item.consultant?.consultantName || this.consultantName || 'Consultant';
        const link = this.buildTimesheetMeta(ts, name, local);
        const amount = this.formatMoney(item.totalAmount);
        if (amount !== '—') {
          link.meta = link.meta === '—' ? amount : `${link.meta} · ${amount}`;
        }

        if (link.hrTimesheetId) {
          if (!hrNoteCache.has(link.hrTimesheetId)) {
            hrNoteCache.set(link.hrTimesheetId, await this.fetchLatestHrNote(link.hrTimesheetId));
          }
          link.note = hrNoteCache.get(link.hrTimesheetId) || '';
        }

        timesheetLinks.push(link);
      }

      if (timesheetLinks.length) {
        const timesheetEventTs =
          itemList.find((item) => item.createdAt)?.createdAt || invoiceCreatedAt;
        this.pushEvent(events, {
          timestamp: timesheetEventTs || invoice.date || transaction.date,
          title:
            timesheetLinks.length === 1
              ? 'Timesheet included on invoice'
              : `${timesheetLinks.length} timesheets included on invoice`,
          type: 'timesheet',
          icon: 'bi bi-calendar-check',
          links: timesheetLinks
        });
      }

      const paymentStatus = transaction.status || invoice.status;
      if (paymentStatus && /paid|partial/i.test(String(paymentStatus))) {
        this.pushEvent(events, {
          timestamp: invoice.updatedAt || invoice.createdAt,
          title: `Invoice marked ${paymentStatus}`,
          type: 'invoice',
          icon: 'bi bi-check2-circle',
          chips: [this.formatMoney(transaction.amount)].filter((c) => c !== '—')
        });
      }

      return events;
    },
    async buildBillAudit(transaction) {
      const events = [];
      const billId = transaction.enterBillId;
      if (!billId) {
        return this.buildGenericAudit(transaction);
      }

      const bill = await ApiService.getEnterBillById(billId);
      const vendorName = bill.vendor?.vendorName || bill.vendorName || transaction.vendorName || '';
      const billNo = bill.billNo || transaction.billNo || `#${billId}`;

      this.pushEvent(events, {
        timestamp: bill.createdAt || transaction.date,
        title: `Bill ${billNo} created`,
        type: 'bill',
        icon: 'bi bi-file-earmark-text',
        chips: [
          vendorName,
          formatDateShort(bill.billDate || transaction.date),
          this.formatMoney(bill.total ?? transaction.amount),
          bill.status || transaction.status
        ].filter(Boolean)
      });

      const consultantRows = Array.isArray(bill.consultantRecords)
        ? bill.consultantRecords
        : Array.isArray(bill.consultantTimesheetRecords)
          ? bill.consultantTimesheetRecords
          : [];

      const links = [];
      const seenTs = new Set();
      const hrNoteCache = new Map();

      for (const row of consultantRows) {
        if (!row.timesheetId || seenTs.has(row.timesheetId)) continue;
        seenTs.add(row.timesheetId);

        const local = this.findLocalTimesheet(row.timesheetId);
        const link = this.buildTimesheetMeta(
          {
            id: row.timesheetId,
            fromDate: local?.fromDate,
            toDate: local?.toDate,
            hours: row.hours ?? local?.hours,
            status: local?.status,
            hrTimesheetId: local?.hrTimesheetId
          },
          row.consultantName,
          local
        );
        const amount = this.formatMoney(row.total ?? row.amount);
        if (amount !== '—') {
          link.meta = link.meta === '—' ? amount : `${link.meta} · ${amount}`;
        }

        if (link.hrTimesheetId) {
          if (!hrNoteCache.has(link.hrTimesheetId)) {
            hrNoteCache.set(link.hrTimesheetId, await this.fetchLatestHrNote(link.hrTimesheetId));
          }
          link.note = hrNoteCache.get(link.hrTimesheetId) || '';
        }

        links.push(link);
      }

      if (links.length) {
        this.pushEvent(events, {
          timestamp: bill.createdAt || transaction.date,
          title:
            links.length === 1
              ? 'Timesheet included on bill'
              : `${links.length} timesheets included on bill`,
          type: 'timesheet',
          icon: 'bi bi-calendar-check',
          links
        });
      }

      return events;
    },
    buildReceivedPaymentAudit(transaction) {
      const events = [];
      const lines = Array.isArray(transaction.receivePaymentLines)
        ? transaction.receivePaymentLines
        : [];
      const links = lines.map((line, i) => ({
        id: `rp-${i}-${line.customerInvoiceId || line.invoiceNo}`,
        title: `Invoice ${line.invoiceNo || line.customerInvoiceId || '—'}`,
        meta: [
          line.consultantName,
          this.formatMoney(line.allocatedAmount ?? line.paymentAmount)
        ]
          .filter(Boolean)
          .join(' · ')
      }));

      this.pushEvent(events, {
        timestamp: transaction.date,
        title: `Payment ${transaction.receivePaymentNo || ''} received`.trim(),
        type: 'payment',
        icon: 'bi bi-cash-stack',
        chips: [
          transaction.customer,
          this.formatMoney(transaction.amount),
          transaction.paymentMethod,
          transaction.status || 'Received'
        ].filter(Boolean),
        links
      });

      return events;
    },
    buildPayBillAudit(transaction) {
      const events = [];
      const lines = Array.isArray(transaction.payLineItems) ? transaction.payLineItems : [];
      const links = lines.map((line, i) => ({
        id: `pb-${i}-${line.billId || line.billNo}`,
        title: `Bill ${line.billNo || line.billId || '—'}`,
        meta: this.formatMoney(line.amountPaid)
      }));

      this.pushEvent(events, {
        timestamp: transaction.date,
        title: `Payment ${transaction.payBillNo || transaction.billNo || ''} sent`.trim(),
        type: 'payment',
        icon: 'bi bi-wallet2',
        chips: [
          transaction.vendorName,
          this.formatMoney(transaction.amount),
          transaction.status
        ].filter(Boolean),
        links
      });

      return events;
    },
    buildGenericAudit(transaction) {
      const events = [];
      this.pushEvent(events, {
        timestamp: transaction.date,
        title: `${transaction.type || 'Transaction'} recorded`,
        type: 'activity',
        icon: 'bi bi-arrow-left-right',
        chips: [
          transaction.invoiceNo || transaction.billNo || (transaction.id != null ? `#${transaction.id}` : ''),
          this.formatMoney(transaction.amount),
          transaction.status
        ].filter(Boolean)
      });
      return events;
    },
    formatMoney(value) {
      if (value === undefined || value === null || value === '') return '—';
      const n = Number(value);
      if (!Number.isFinite(n)) return String(value);
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
    }
  }
};
</script>
